import { useState } from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PhoneInput } from "@/components/ui/phone-input";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/query-client";
import { CompanyContactCombobox } from "./company-contact-combobox";

interface ContactOption {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  companyName: string | null;
  contactType: string;
}

interface ContactComboboxProps {
  /** If set, only show contacts of this type (e.g. "contractor") */
  type?: string;
  /** Currently selected contact ID */
  value?: string | null;
  /** Called with the full contact when a selection is made */
  onSelect: (contact: ContactOption | null) => void;
  excludeIds?: string[];
  placeholder?: string;
  /** If true, show an inline "Create new referrer" option */
  allowCreate?: boolean;
  /** If set, filters contacts by companyId (for two-step picker) */
  companyId?: string | null;
}

export function ContactCombobox({
  type,
  value,
  onSelect,
  excludeIds = [],
  placeholder = "Search contacts...",
  allowCreate = false,
  companyId,
}: ContactComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [newContact, setNewContact] = useState({ firstName: "", lastName: "", email: "", phone: "", companyId: null as string | null });
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ["/api/contacts", "combobox", search, type, companyId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (type) params.set("type", type);
      if (companyId) params.set("companyId", companyId);
      const res = await apiRequest("GET", `/api/contacts?${params}`);
      return res.json() as Promise<{ contacts: ContactOption[] }>;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof newContact) => {
      const res = await apiRequest("POST", "/api/contacts", {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        companyId: data.companyId,
        contactType: type ?? "other",
      });
      return res.json() as Promise<ContactOption>;
    },
    onSuccess: (contact) => {
      queryClient.invalidateQueries({ queryKey: ["/api/contacts"] });
      onSelect(contact);
      setCreateOpen(false);
      setOpen(false);
      setNewContact({ firstName: "", lastName: "", email: "", phone: "", companyId: companyId ?? null });
    },
  });

  const allOptions = (data?.contacts ?? []).filter((c) => !excludeIds.includes(c.id));
  const selected = allOptions.find((c) => c.id === value) ?? (value ? { id: value } as any : null);

  const getLabel = (c: ContactOption) =>
    [c.firstName, c.lastName].filter(Boolean).join(" ") || c.companyName || c.email || "Unnamed";

  const selectedLabel = selected ? getLabel(selected) : null;

  const exactMatch = allOptions.some(
    (c) => getLabel(c).toLowerCase() === search.trim().toLowerCase()
  );
  const canCreate = allowCreate && search.trim().length > 0 && !exactMatch;

  const openCreateWithSearch = () => {
    setNewContact((p) => ({ ...p, firstName: search.trim(), companyId: companyId ?? p.companyId }));
    setOpen(false);
    setCreateOpen(true);
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
          >
            <span className={cn(!selectedLabel && "text-muted-foreground")}>
              {selectedLabel ?? placeholder}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0">
          <Command>
            <CommandInput
              placeholder="Search by name..."
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              <CommandEmpty>
                {canCreate ? (
                  <button
                    className="flex items-center gap-2 px-2 py-1.5 text-sm w-full hover:bg-accent"
                    onClick={openCreateWithSearch}
                  >
                    <Plus className="h-4 w-4" />
                    Add &quot;{search.trim()}&quot;
                  </button>
                ) : (
                  "No contacts found."
                )}
              </CommandEmpty>
              <CommandGroup>
                {selected && (
                  <CommandItem
                    value="__clear__"
                    onSelect={() => { onSelect(null); setOpen(false); setSearch(""); }}
                  >
                    <Check className="mr-2 h-4 w-4 opacity-0" />
                    <span className="text-muted-foreground">Clear selection</span>
                  </CommandItem>
                )}
                {allOptions.map((contact) => {
                  const label = getLabel(contact);
                  return (
                    <CommandItem
                      key={contact.id}
                      value={label}
                      onSelect={() => {
                        onSelect(contact);
                        setOpen(false);
                        setSearch("");
                      }}
                    >
                      <Check className={cn("mr-2 h-4 w-4", contact.id === value ? "opacity-100" : "opacity-0")} />
                      <span>{label}</span>
                      {!type && (
                        <span className="ml-2 text-xs text-muted-foreground capitalize">{contact.contactType}</span>
                      )}
                    </CommandItem>
                  );
                })}
                {canCreate && allOptions.length > 0 && (
                  <CommandItem
                    value={`__create__${search}`}
                    onSelect={openCreateWithSearch}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add &quot;{search.trim()}&quot;
                  </CommandItem>
                )}
                {allowCreate && !canCreate && (
                  <CommandItem
                    value="__create_new__"
                    onSelect={() => { setNewContact((p) => ({ ...p, companyId: companyId ?? p.companyId })); setOpen(false); setCreateOpen(true); }}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    <span>New {type ?? "contact"}...</span>
                  </CommandItem>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {allowCreate && (
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle>New {type ? type.charAt(0).toUpperCase() + type.slice(1) : "Contact"}</DialogTitle>
            </DialogHeader>
            <div className="grid gap-3 py-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="nc-first">First Name</Label>
                  <Input id="nc-first" value={newContact.firstName} onChange={(e) => setNewContact((p) => ({ ...p, firstName: e.target.value }))} />
                </div>
                <div>
                  <Label htmlFor="nc-last">Last Name</Label>
                  <Input id="nc-last" value={newContact.lastName} onChange={(e) => setNewContact((p) => ({ ...p, lastName: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label>Company</Label>
                <CompanyContactCombobox
                  value={newContact.companyId}
                  onSelect={(c) => setNewContact((p) => ({ ...p, companyId: c?.id ?? null }))}
                  allowCreate
                />
              </div>
              <div>
                <Label htmlFor="nc-email">Email</Label>
                <Input id="nc-email" type="email" value={newContact.email} onChange={(e) => setNewContact((p) => ({ ...p, email: e.target.value }))} />
              </div>
              <div>
                <Label htmlFor="nc-phone">Phone</Label>
                <PhoneInput
                  value={newContact.phone}
                  onChange={(value) => setNewContact((p) => ({ ...p, phone: value }))}
                />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                <Button
                  onClick={() => createMutation.mutate(newContact)}
                  disabled={createMutation.isPending || (!newContact.firstName && !newContact.companyId)}
                >
                  {createMutation.isPending ? "Creating..." : "Create"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
